import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Award, CheckCircle, Download, Home, RotateCcw, Share2, BookOpen, ExternalLink, Loader2, User, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useProgress } from '../context/ProgressContext';
import { usePurchase } from '../context/PurchaseContext';
import { useAuth } from '../context/AuthContext';
import { courseModules } from '../data/courseData';
import { toast } from 'sonner';

export default function CertificatePage() {
  const navigate = useNavigate();
  const { progress, getOverallProgress, resetProgress } = useProgress();
  const { testerMode, hasPurchased, hasAccess } = usePurchase();
  const { isAuthenticated, user, authFetch } = useAuth();
  const certificateRef = useRef(null);

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [showNameConfirm, setShowNameConfirm] = useState(false);
  const [enteredName, setEnteredName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [issueError, setIssueError] = useState(null);

  const isTesterMode = testerMode.isActive && !hasPurchased;
  const overallProgress = getOverallProgress();
  const isComplete = overallProgress === 100;

  // Fetch existing certificate if any
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await authFetch('/api/user/certificate');
        if (resp.ok) {
          const data = await resp.json();
          setCertificate(data.certificate);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, authFetch]);

  const openNameModal = () => {
    setEnteredName(user?.full_name || user?.first_name || '');
    setConfirmed(false);
    setIssueError(null);
    setShowNameConfirm(true);
  };

  const submitCertificate = async () => {
    const name = enteredName.trim();
    if (name.length < 2) {
      setIssueError('Please enter your full name.');
      return;
    }
    if (!confirmed) {
      setIssueError('Please tick the box to confirm the spelling.');
      return;
    }
    setIssuing(true);
    setIssueError(null);
    try {
      const resp = await authFetch('/api/user/certificate/issue', {
        method: 'POST',
        body: JSON.stringify({ full_name: name }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setIssueError(data?.detail || 'Could not issue certificate.');
        return;
      }
      setCertificate(data.certificate);
      setShowNameConfirm(false);
      toast.success('Certificate issued! Well done.');
    } catch (e) {
      setIssueError('Could not reach the server. Please try again.');
    } finally {
      setIssuing(false);
    }
  };

  const handlePrint = () => window.print();

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Digital Safety Certificate',
          text: "I've completed the Digital Safety Course and earned my certificate!",
          url: window.location.href,
        });
      } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(`I've completed the Digital Safety Course! ${window.location.origin}`);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleRestartCourse = async () => {
    if (!window.confirm('Restart the course? Your checklists, notes and quiz progress will be reset so you can practise again. Your issued certificate stays safe.')) return;
    await resetProgress();
    navigate('/module/introduction');
    toast.success('Course restarted — your certificate is still yours.');
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
        <p className="text-text-muted">Loading certificate…</p>
      </div>
    );
  }

  // Not logged in or no access
  if (!isAuthenticated) {
    return (
      <div className="max-w-xl mx-auto text-center fade-in">
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-8 md:p-12">
          <Award className="w-14 h-14 text-stone-400 mx-auto mb-4" />
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-3">Sign in to view your certificate</h1>
          <p className="text-text-muted mb-6">Your certificate lives on your account. Sign in to pick up where you left off.</p>
          <Link to="/login"><Button className="rounded-full px-8 py-5 text-lg font-bold">Sign in</Button></Link>
        </div>
      </div>
    );
  }

  // Already issued certificate — display it (even if progress was reset)
  if (certificate) {
    const issuedDate = new Date(certificate.issued_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    return (
      <div className="max-w-4xl mx-auto fade-in">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 no-print">
          <Link to="/"><Button variant="outline" className="rounded-full px-6 py-4 font-semibold"><Home className="w-4 h-4 mr-2" />Home</Button></Link>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={handleShare} className="rounded-full px-6 py-4 font-semibold"><Share2 className="w-4 h-4 mr-2" />Share</Button>
            <Button onClick={handlePrint} className="bg-primary text-white hover:bg-primary-600 rounded-full px-6 py-4 font-semibold"><Download className="w-4 h-4 mr-2" />Print Certificate</Button>
          </div>
        </div>

        <div ref={certificateRef} className="certificate bg-gradient-to-br from-secondary-50 via-white to-secondary-50 rounded-3xl p-8 md:p-12 shadow-xl relative overflow-hidden" data-testid="certificate">
          {isTesterMode && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="text-[120px] md:text-[180px] font-extrabold text-red-500/20 transform -rotate-45 select-none" style={{ letterSpacing: '0.2em' }}>TEST</div>
            </div>
          )}
          <div className="border-4 border-double border-secondary/50 rounded-2xl p-6 md:p-10">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-secondary rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                <Award className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-primary mb-2">Certificate of Completion</h1>
              <p className="text-text-muted">Digital Safety Course</p>
            </div>
            <div className="text-center mb-8">
              <p className="text-text-muted text-lg mb-2">This certifies that</p>
              <div className="border-b-2 border-primary/30 pb-2 mb-6 mx-auto max-w-md">
                <p className="font-heading font-bold text-2xl text-primary" data-testid="certificate-name">{certificate.full_name}</p>
              </div>
              <p className="text-text-muted text-lg mb-6">has successfully completed all modules of the</p>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">Australian Digital Safety Course</h2>
              <p className="text-text-muted max-w-xl mx-auto">
                demonstrating understanding of essential online safety practices including password security, device protection, scam awareness, and privacy management.
              </p>
            </div>
            <div className="bg-white/50 rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <h3 className="font-heading font-bold text-lg text-text mb-4 text-center">Modules Completed</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {courseModules.map((module) => (
                  <div key={module.id} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-text-muted">{module.shortTitle}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center">
              <p className="text-text-muted mb-2">Completed on</p>
              <p className="font-heading font-bold text-xl text-primary mb-2">{issuedDate}</p>
              <p className="text-xs text-text-muted mb-4">Certificate ID: <span className="font-mono">{certificate.certificate_id}</span></p>
              <div className="flex items-center justify-center gap-3 text-text-muted text-sm">
                <img src="https://customer-assets.emergentagent.com/job_aussiesafety/artifacts/17uanq9m_BTA_Logo_NoBgnd.png" alt="Bellarine Tech Assist Logo" className="w-8 h-8 object-contain" />
                <span>Issued by</span>
                <span className="font-semibold">Bellarine Tech Assist</span>
              </div>
              {isTesterMode && (
                <div className="mt-4 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg inline-block">
                  Preview certificate — purchase the course for an official certificate
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 no-print space-y-6">
          <div className="bg-success/10 border border-success/30 rounded-2xl p-6 text-center">
            <h2 className="font-heading font-bold text-xl text-success mb-2">Well Done!</h2>
            <p className="text-text-muted mb-4">You've completed the Digital Safety Course. Online safety is an ongoing practice — keep using what you've learned.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/"><Button variant="outline" className="rounded-full px-6 py-4 font-semibold"><Home className="w-4 h-4 mr-2" />Revisit Modules</Button></Link>
              <Button variant="outline" onClick={handleRestartCourse} data-testid="restart-course" className="rounded-full px-6 py-4 font-semibold"><RotateCcw className="w-4 h-4 mr-2" />Restart Course</Button>
            </div>
            <p className="text-xs text-text-muted mt-3">Your certificate stays safe when you restart.</p>
          </div>
        </div>
      </div>
    );
  }

  // No cert yet — needs completion + access
  if (!hasAccess || !isComplete) {
    return (
      <div className="max-w-2xl mx-auto text-center fade-in">
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-8 md:p-12">
          <div className="w-20 h-20 bg-stone-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Award className="w-10 h-10 text-stone-400" />
          </div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">Certificate Locked</h1>
          <p className="text-text-muted text-lg mb-6">
            {!hasAccess ? 'Unlock the full course to earn your certificate.' : `Pass all 12 module quizzes to unlock your certificate.`}
          </p>
          <div className="bg-stone-50 rounded-xl p-4 mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-text-muted">Your progress</span>
              <span className="font-bold text-primary">{overallProgress}%</span>
            </div>
            <div className="h-4 bg-stone-200 rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${overallProgress}%` }} />
            </div>
            <p className="text-sm text-text-muted mt-2">{(progress.completedModules || []).length} of 12 modules completed</p>
          </div>
          {!hasAccess ? (
            <Link to="/purchase"><Button className="rounded-full px-8 py-5 text-lg font-bold">Unlock the course</Button></Link>
          ) : (
            <Link to={`/module/${courseModules[(progress.completedModules || []).length]?.slug || 'introduction'}`}>
              <Button className="rounded-full px-8 py-5 text-lg font-bold">Continue Learning</Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Eligible but no cert — show "Claim Certificate" with name confirm
  return (
    <div className="max-w-2xl mx-auto text-center fade-in">
      <div className="bg-white rounded-3xl border-2 border-success/30 p-8 md:p-12">
        <div className="w-20 h-20 bg-success/10 rounded-full mx-auto mb-6 flex items-center justify-center">
          <Award className="w-10 h-10 text-success" />
        </div>
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-3">You've earned your certificate</h1>
        <p className="text-text-muted text-lg mb-6">All 12 modules complete. Before we issue it, we need the exact name to print on your certificate.</p>
        <Button onClick={openNameModal} className="rounded-full px-8 py-5 text-lg font-bold" data-testid="claim-certificate">
          Claim my certificate
        </Button>
      </div>

      {showNameConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8 shadow-2xl fade-in text-left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <h2 className="font-heading font-bold text-xl text-text">Confirm your name</h2>
            </div>
            <p className="text-text-muted mb-5">
              This name will appear on your certificate — please check the spelling carefully. It cannot be edited by you after it's issued.
            </p>
            <label className="block text-base font-semibold text-text mb-2">Full name</label>
            <Input
              data-testid="cert-name-input"
              value={enteredName}
              onChange={(e) => setEnteredName(e.target.value)}
              placeholder="e.g. Margaret Anne Johnson"
              className="text-lg py-5"
              autoFocus
            />
            <label className="flex items-start gap-2 mt-4 text-sm text-text-muted cursor-pointer">
              <input
                data-testid="cert-name-confirm"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1"
              />
              <span>I've checked the spelling and I'm happy with this name on my certificate.</span>
            </label>
            {issueError && (
              <div className="mt-3 bg-error/10 border border-error/30 text-error rounded-xl p-3 text-sm flex items-start gap-2" data-testid="cert-name-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{issueError}</span>
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowNameConfirm(false)} className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={submitCertificate} disabled={issuing} data-testid="cert-name-submit" className="flex-1 rounded-xl bg-primary text-white">
                {issuing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Issue my certificate
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
